import React, { useState } from "react";
import { Application } from "../types";
import { useTranslation } from "react-i18next";
import { Button, Card, Label, SemanticCOLORS } from "semantic-ui-react";
import company from "../../../icons/company.svg";
import location from "../../../icons/location.svg";
import date from "../../../icons/date.svg";
import time from "../../../icons/time.svg";
import { ApplicationStatus } from "../../../app/enums/applicationStatus";
import { prettifyDate, prettifyDateWithoutTime, prettifyDuration } from "../../../app/helpers/dateHelpers";
import { Link } from "react-router-dom";
import ResponsiblePersonModal from "../../shadowings/components/ResponsiblePersonModal";
import ReflectionModal from "../../reflections/ReflectionModal";
import { isExistingReflection } from "../../../app/helpers/reflections";
import ReflectionPreviewModal from "../../reflections/ReflectionPreviewModal";
import agent from "../../../app/api/agent";
import ApplicationModal from "./ApplicationModal";

interface IProps {
  application: Application;
  past: boolean;
  handleCancel(applicationId: number): void;
  handleResubmitted(): void;
}

const COLOR_STATUS_MAP: { [key: string]: ApplicationStatus[] } = {
  purple: [
    ApplicationStatus.WAITING_FOR_SCHOOL_APPROVAL,
    ApplicationStatus.WAITING_FOR_COMPANY_APPROVAL,
    ApplicationStatus.WAITING_FOR_ADMIN_APPROVAL,
  ],
  red: [ApplicationStatus.COMPANY_REJECTED, ApplicationStatus.ADMIN_REJECTED, ApplicationStatus.STUDENT_CANCELLED],
  green: [ApplicationStatus.COMPANY_APPROVED],
};

// Returning for editing and cancelling both set SchoolCancelled; a cancel always has this reason.
const SCHOOL_CANCEL_REASON = "Mokykla atšaukė aplikaciją.";

const ApplicationItem: React.FC<IProps> = ({ application, handleCancel, handleResubmitted, past }) => {
  const { t } = useTranslation();
  const [isResubmitting, setResubmitting] = useState(false);

  const [reflectionExists, setReflectionExists] = useState(isExistingReflection(application.reflections));

  const isReturnedForEdit =
    application.status === ApplicationStatus.SCHOOL_CANCELLED &&
    !!application.statusReason &&
    application.statusReason !== SCHOOL_CANCEL_REASON;

  const getLabelColor = () =>
    isReturnedForEdit
      ? "orange"
      : ((Object.keys(COLOR_STATUS_MAP).find((key) => COLOR_STATUS_MAP[key].includes(application.status)) ||
          "red") as SemanticCOLORS);

  const renderCancelButton = () => {
    const showButton =
      [
        ApplicationStatus.COMPANY_APPROVED,
        ApplicationStatus.WAITING_FOR_SCHOOL_APPROVAL,
        ApplicationStatus.WAITING_FOR_COMPANY_APPROVAL,
        ApplicationStatus.WAITING_FOR_ADMIN_APPROVAL,
      ].includes(application.status) && Date.parse(application.shadowing.date) > Date.now();

    return showButton ? (
      <Link to="#" onClick={() => handleCancel(application.id)}>
        {t("common.actions.cancel")}
      </Link>
    ) : null;
  };

  const renderLocation = () => {
    return application.shadowing.online
      ? t("shadowing.remote")
      : `${application.shadowing.city}, ${application.shadowing.address}`;
  };

  const handleReflectionSubmit = () => {
    setReflectionExists(true);
  };

  const renderReflectionButton = () => {
    const showButton = application.status === ApplicationStatus.COMPANY_APPROVED && past && !application.noShow;
    if (!showButton) {
      return null;
    }
    if (reflectionExists) {
      const loadReflectionFn = () => agent.ApplicationReflections.get(application.id);
      return (
        <ReflectionPreviewModal title="reflection.modal_title" reflections={loadReflectionFn as any}>
          <Button basic style={{ whiteSpace: "nowrap" }}>
            {t("reflection.view")}
          </Button>
        </ReflectionPreviewModal>
      );
    }

    return (
      <ReflectionModal applicationId={application.id} handleReflectionSubmit={handleReflectionSubmit}>
        <Button primary style={{ whiteSpace: "nowrap" }}>
          {t("reflection.fill")}
        </Button>
      </ReflectionModal>
    );
  };

  return (
    <Card>
      <Card.Content>
        <div className="student-application-item__logo">
          <img src={application.shadowing.company.logoUrl || "/assets/logo_placeholder.png"} alt="logo" />
        </div>

        <Card.Header>
          {application.shadowing.position}
          {application.shadowing.isInternship && (
            <Label color="teal" size="tiny" style={{ marginLeft: "0.5em", verticalAlign: "middle" }}>
              {t("common.internship")}
            </Label>
          )}
        </Card.Header>
        <Card.Meta>
          <div>
            <img src={location} alt="location" />
            {renderLocation()}
          </div>
          <div>
            <img src={company} alt="company" />
            {application.shadowing.company.name}
          </div>
          <div>
            <span className="student-application-item__meta-part">
              <img src={date} alt="date" />
              {application.shadowing.isInternship && application.shadowing.endDate
                ? `${prettifyDateWithoutTime(application.shadowing.date)} – ${prettifyDateWithoutTime(application.shadowing.endDate)}`
                : prettifyDate(application.shadowing.date)}
            </span>
            {!application.shadowing.isInternship && !!application.shadowing.visitDuration && (
              <span className="student-application-item__meta-part">
                <img src={time} alt="duration" />
                {t("shadowing.visit_duration")}: {prettifyDuration(application.shadowing.visitDuration)}
              </span>
            )}
          </div>
        </Card.Meta>

        <ResponsiblePersonModal item={application.shadowing} />

        <Card.Description>
          <Label color={getLabelColor()} circular>
            {t(`applications.status.${isReturnedForEdit ? "ReturnedForEdit" : application.status}`)}
          </Label>
          {application.noShow && (
            <Label color="orange" circular>
              {t("applications.student.no_show")}
            </Label>
          )}
          {isReturnedForEdit && (
            <p style={{ marginTop: "0.5em", color: "#666", fontSize: "0.9em" }}>
              <strong>{t("applications.school.return_reason_label")}:</strong> {application.statusReason}
            </p>
          )}
          {renderCancelButton()}
          {isReturnedForEdit && Date.parse(application.shadowing.date) > Date.now() && (
            <Link to="#" onClick={() => setResubmitting(true)} style={{ display: "block", marginTop: "0.5em" }}>
              {t("applications.student.resubmit")}
            </Link>
          )}
          {isResubmitting && (
            <ApplicationModal
              shadowing={application.shadowing}
              initialValues={{
                motivationLetter: application.motivationLetter ?? "",
                linkedinUrl: application.linkedinUrl ?? "",
                allowPhotos: !!application.allowPhotos,
              }}
              onCancel={() => setResubmitting(false)}
              onSuccess={() => {
                setResubmitting(false);
                handleResubmitted();
              }}
            />
          )}
          {renderReflectionButton()}
        </Card.Description>
      </Card.Content>
    </Card>
  );
};

export default ApplicationItem;
